import type { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import type { AppState } from '~/store/app.store';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { getSplitItemSettingsFromUserDataSettings } from './userDataSettings.helpers';

export const userDataSettingsSelector = (state: AppState) => state.userData.settings;
export const useUserDataSettingsSelector = () => useSelector(userDataSettingsSelector);

export const userDataIsReadySelector = (state: AppState) => state.userData.isReady;

export const currentClientCurrentMapSplitItemSettingsSelector = createStateSelector([
  userDataSettingsSelector,
  clientIdSelector,
  mapIdSelector,
], (userDataSettingsState, clientId, mapId) => {
  return getSplitItemSettingsFromUserDataSettings(userDataSettingsState, clientId, mapId);
});

export const useSplitItemSettingsSelector = (clientId: number | null, mapId: number | null) => {
  const userDataSettingsState = useSelector(userDataSettingsSelector);
  return getSplitItemSettingsFromUserDataSettings(userDataSettingsState, clientId, mapId);
};

export const useUserDataSettingsToolCollapseSelector = (clientId: number | null, mapId: number | null, mapTool: MapTool | null) => {
  const userDataSettingsState = useUserDataSettingsSelector();

  if (!clientId || !mapId || !mapTool) {
    return undefined;
  }

  return userDataSettingsState?.[clientId]?.toolsCollapse?.[mapId]?.[mapTool];
};

export const useUserSettingsIsBoundaryEditAllowed = (clientId: number | null) => {
  const userDataSettingsState = useUserDataSettingsSelector();

  return clientId === null ? false : userDataSettingsState?.[clientId]?.allowEditingBoundaryGroups ?? false;
};

export const useUserSettingsConfirmedMigrationWarning = (clientId: number | null) => {
  const userDataSettingsState = useUserDataSettingsSelector();
  return clientId === null ? false : userDataSettingsState?.[clientId]?.confirmedMigrationWarning ?? false;
};

export const useUserSettingsShownWelcomeModal = (clientId: number | null) => {
  const userDataSettingsState = useUserDataSettingsSelector();
  return clientId === null ? false : userDataSettingsState?.[clientId]?.shownWelcomeModal ?? false;
};
